import type { ImageStitchSeoData } from './image-stitch-seo-data';

export const ES_IMAGE_STITCH_DATA: ImageStitchSeoData = {
  path: '/image-stitch',
  seoTitle: 'Unir imágenes online | Combinar 2-9 fotos',
  h1: 'Unir imágenes: combina 2 a 9 fotos en horizontal, vertical o cuadrícula',
  definitionH2:
    '¿Cómo mantiene este unidor de imágenes cada fuente en su propia celda?',
  seoDescription:
    'Unir imágenes combina 2 a 9 archivos JPG, PNG o WebP en el navegador en un PNG o JPEG. Horizontal, vertical o cuadrícula. Los archivos se quedan en local.',
  keywords: [
    'unir imágenes',
    'unir fotos',
    'combinar fotos',
    'fotos lado a lado',
    'unir capturas de pantalla',
    'cuadrícula de fotos',
    'image stitcher',
    'unir imágenes sin subir',
    'unidor de imágenes',
  ].join(', '),
  datePublished: '2026-09-07',
  lastUpdated: '2026-09-18',
  lastUpdatedLabel: '18 de septiembre de 2026',
  quickAnswer:
    'Unir imágenes combina de dos a nueve archivos JPG, PNG o WebP en tu navegador en un único PNG o JPEG. Elige un diseño horizontal, vertical o en cuadrícula. Los archivos originales no se envían a una API de subida.',
  proofImage: '/image-stitch/image-stitcher-horizontal-16-9-proof.png',
  proofWidth: 1952,
  proofHeight: 360,
  proofCaption:
    'Esta exportación de unir imágenes usa tres fotogramas 16:9 en diseño Horizontal, ajuste Cover, hueco blanco de 16 px y PNG.',
  proofCanvas: 'El lienzo de ejemplo mide 1952 x 360 píxeles.',
  typicalTime:
    'Tiempo típico: menos de un minuto después de añadir dos archivos.',
  steps: [
    {
      name: 'Añade de dos a nueve imágenes',
      text: 'Elige archivos JPG, PNG o WebP de hasta 20 MB cada uno. El unidor de imágenes los lee en el navegador y las teselas numeradas muestran el orden del lienzo final.',
    },
    {
      name: 'Elige un diseño horizontal, vertical o en cuadrícula',
      text: 'Usa una fila horizontal para comparaciones, una columna vertical para imágenes largas o una cuadrícula para una galería compacta. Mueve cualquier fuente antes de exportar.',
    },
    {
      name: 'Ajusta ratio, ajuste, hueco y fondo',
      text: 'Selecciona celdas 1:1, 4:5 o 16:9. Cover rellena cada celda recortando bordes; Contain conserva la imagen completa y usa el color de fondo alrededor.',
    },
    {
      name: 'Previsualiza y descarga la imagen unida',
      text: 'Revisa la vista previa en vivo y exporta el lienzo como PNG o JPEG. La descarga se crea en local, sin enviar los archivos a una API de subida.',
    },
  ],
  features: [
    {
      title: 'Tres direcciones de diseño',
      body: 'Coloca las imágenes en una fila horizontal, una columna vertical o una cuadrícula equilibrada automáticamente. El unidor de imágenes admite de 2 a 9 celdas y recalcula el lienzo cuando cambia el orden de las fuentes.',
    },
    {
      title: 'Encaje predecible de cada celda',
      body: 'Elige celdas cuadradas, retrato 4:5 o paisaje 16:9. Usa Contain para conservar la fuente completa o Cover para llenar cada celda con un recorte de borde.',
    },
    {
      title: 'Control de hueco y lienzo',
      body: 'Define el espacio de 0 a 64 píxeles y elige cualquier color de fondo. Ese ajuste sirve para comparaciones limpias, mood boards oscuros y tiras de capturas unidas.',
    },
    {
      title: 'Exportaciones de alta resolución',
      body: 'Descarga PNG cuando las capturas, el texto o los diagramas deban seguir nítidos, o JPEG cuando importe más un archivo de foto más ligero. La exportación se limita a 8.192 píxeles en el lado más largo.',
    },
  ],
  useCases: [
    {
      title: 'Comparaciones antes y después',
      input:
        'Dos retoques, versiones de producto o fotos de progreso que hay que juzgar en un solo marco.',
      action:
        'En el unidor de imágenes, ponlas lado a lado en un diseño Horizontal, con celdas iguales y un hueco pequeño, para comparar sin abrir archivos separados.',
      result:
        'Una imagen ancha de comparación que deja cada fuente en su propio panel.',
    },
    {
      title: 'Referencias largas de capturas',
      input:
        'Capturas, pasos o tomas secuenciales que deben leerse de arriba abajo.',
      action:
        'En el unidor de imágenes, añade los archivos en orden de lectura, elige un diseño Vertical y Contain, y deja el hueco en cero o en un divisor estrecho.',
      result: 'Una imagen larga para revisión, anotación o documentación.',
    },
    {
      title: 'Mood boards y hojas de contacto',
      input:
        'De cuatro a nueve referencias que deben tener el mismo peso visual.',
      action:
        'En el unidor de imágenes, elige una cuadrícula y un ratio coherente para producir una vista compacta.',
      result:
        'Un único archivo de galería para un brief, una presentación o un borrador social.',
    },
  ],
  limits: [
    {
      title: 'Es una herramienta de diseño, no un software de panorama',
      body: 'El unidor de imágenes no alinea fotogramas de panorama superpuestos, no funde costuras, no quita fondos, no añade etiquetas y no inventa contenido faltante entre las fuentes.',
    },
    {
      title: 'Cover puede recortar bordes importantes',
      body: 'Revisa los recortes Cover en caras, texto, logotipos y el borde de productos antes de descargar. Usa Contain cuando la fuente completa deba seguir visible.',
    },
    {
      title: 'Los diseños muy grandes se reducen',
      body: 'Las filas o columnas de nueve imágenes se reducen para mantener el lado más largo de exportación en 8.192 píxeles o menos. La vista previa usa un lienzo más pequeño de 1.600 píxeles.',
    },
    {
      title: 'Elige PNG o JPEG según el uso',
      body: 'Usa PNG para capturas de interfaz, diagramas y ediciones posteriores. JPEG usa una calidad del 92 % y encaja mejor cuando importa un archivo de foto más ligero.',
    },
    {
      title: 'Los archivos descargados no se guardan en Historial',
      body: 'Todas las imágenes seleccionadas se quedan en la pestaña del navegador durante este flujo. El lienzo descargado no se guarda en Historial: conserva el archivo en tu dispositivo.',
    },
  ],
  faqs: [
    {
      question: '¿Qué es un unidor de imágenes?',
      answer:
        'Un unidor de imágenes combina varios archivos de imagen en un lienzo más grande. Esta herramienta coloca de 2 a 9 imágenes en fila horizontal, columna vertical o cuadrícula. No usa IA para inventar o sustituir píxeles.',
    },
    {
      question: '¿Se pueden unir imágenes sin subirlas?',
      answer:
        'Sí. El unidor de imágenes lee, ordena, previsualiza y exporta las imágenes en la pestaña actual del navegador. Este flujo no envía tus archivos seleccionados al servidor del sitio.',
    },
    {
      question: '¿Cómo poner dos fotos lado a lado?',
      answer:
        'Añade dos fotos, selecciona Horizontal, elige un ratio de celda y Cover o Contain. Ajusta el hueco, comprueba la vista previa y descarga las dos fotos en un PNG o JPEG.',
    },
    {
      question: '¿Unir imágenes reduce la calidad?',
      answer:
        'La exportación usa un lienzo de alta resolución de hasta 8.192 píxeles en el lado más largo. Si un diseño supera ese límite, el unidor de imágenes reduce el lienzo entero. JPEG es compresión con pérdida: elige PNG cuando el texto y los bordes de interfaz deban seguir nítidos.',
    },
    {
      question: '¿Es lo mismo que un panorama o una fusión con IA?',
      answer:
        'No. Un software de panorama alinea fotos superpuestas. Un editor de imagen IA puede generar una composición nueva a partir de varias referencias. Este unidor de imágenes mantiene las fuentes en celdas separadas y une esas celdas en un lienzo. Usa el editor de imagen IA cuando necesites una composición generada de nuevo.',
    },
  ],
  layoutGuide: [
    {
      layout: 'Horizontal',
      input: 'De dos a cinco comparaciones o momentos',
      output: 'Imagen ancha lado a lado',
      fit: 'Cover para paneles iguales',
    },
    {
      layout: 'Vertical',
      input: 'Capturas, pasos o referencias secuenciales',
      output: 'Imagen larga de arriba abajo',
      fit: 'Contain para capturas completas',
    },
    {
      layout: 'Cuadrícula',
      input: 'De cuatro a nueve fotos de igual peso',
      output: 'Galería compacta u hoja de contacto',
      fit: 'Cover para celdas coherentes',
    },
  ],
  specs: [
    { label: 'Formatos de entrada', value: 'JPG, PNG y WebP' },
    { label: 'Número de imágenes', value: 'De 2 a 9 imágenes' },
    { label: 'Tamaño de archivo', value: 'Hasta 20 MB por imagen' },
    { label: 'Diseños', value: 'Horizontal, vertical y cuadrícula' },
    { label: 'Ratios de celda', value: '1:1, 4:5 y 16:9' },
    { label: 'Hueco', value: 'De 0 a 64 píxeles' },
    {
      label: 'Fondo',
      value: 'Color sólido; no hay salida transparente',
    },
    { label: 'Descargas', value: 'PNG o JPEG' },
    {
      label: 'Tamaño de vista previa',
      value: 'Hasta 1.600 píxeles en el lado más largo',
    },
    {
      label: 'Salida máxima',
      value: '8.192 píxeles en el lado más largo',
    },
    { label: 'Calidad JPEG', value: 'Ajuste de calidad del 92 %' },
    { label: 'Formatos no admitidos', value: 'GIF, HEIC y SVG' },
    {
      label: 'Procesamiento',
      value:
        'Pestaña local del navegador; los archivos fuente no se envían a una API de subida',
    },
    {
      label: 'Requisito del navegador',
      value: 'JavaScript y soporte de canvas HTML',
    },
  ],
  schemaFeatures: [
    'Diseños de imagen horizontal, vertical y en cuadrícula',
    'De 2 a 9 fuentes JPG, PNG y WebP',
    'Ratios de celda 1:1, 4:5 y 16:9',
    'Ajuste de imagen Contain y Cover',
    'Vista previa local en el navegador y descarga PNG o JPEG',
  ],
  copy: {
    relatedTools: {
      heading: 'Más herramientas de imagen',
      description:
        'Sigue con un flujo de imagen vecino. Genera una composición nueva, edita una fuente, agranda la exportación o anima la imagen fija una vez unida.',
      ariaLabel: 'Continuar con un flujo de imagen vecino',
      items: [
        {
          alt: 'Póster de ejemplo de generación de imagen Grok Imagine',
          detail: 'Crear una nueva imagen fuente.',
          href: '/image-generator',
          image: '/image-generator/grok-imagine-demo-poster.jpg',
          label: 'Generar una imagen',
        },
        {
          alt: 'Collage del editor de imagen IA Grok con varios estilos retocados',
          detail: 'Editar una fuente o el lienzo terminado.',
          href: '/image-editor',
          image: '/image-editor/grok-image-editor-demo-collage.jpg',
          label: 'Editar una imagen',
        },
        {
          alt: 'Comparación de upscale IA entre una foto fuente y un resultado más grande',
          detail: 'Aumentar la resolución después de exportar.',
          href: '/image-upscale',
          image: '/image-upscale/old-home-video-first-frame-after.jpg',
          label: 'Ampliar una imagen',
        },
        {
          alt: 'Imagen fija preparada como fotograma inicial para un vídeo IA',
          detail: 'Animar la imagen fija terminada.',
          href: '/image-to-video',
          image: '/image-to-video/case-studies/start-frame-after.jpg',
          label: 'Convertir la imagen en vídeo',
        },
      ],
    },
    breadcrumb: 'Unidor de imágenes',
    quickAnswer: {
      eyebrow: 'Unir imágenes - respuesta rápida',
      afterAnswer:
        'Usa el unidor de imágenes cuando ya tienes visuales terminados y necesitas un lienzo predecible. Es un paso de diseño antes de editar con Grok, hacer upscale o image-to-video, no un combinador generativo que inventa píxeles entre las fuentes.',
      publishedPrefix: 'Publicado el 7 de septiembre de 2026 · Actualizado el',
      facts: [
        { label: 'Imágenes', value: 'De 2 a 9 imágenes' },
        { label: 'Formatos', value: 'JPG, PNG y WebP' },
        {
          label: 'Diseños',
          value: 'Horizontal, vertical o en cuadrícula',
        },
        {
          label: 'Procesamiento',
          value: 'Procesamiento local en el navegador',
        },
      ],
    },
    definition: {
      eyebrow: 'Definición',
      description:
        'Un unidor de imágenes es una herramienta de diseño en el navegador que coloca de 2 a 9 imágenes JPG, PNG o WebP en un lienzo. Crea filas horizontales, tiras verticales o cuadrículas, mantiene cada fuente en una celda separada y procesa los archivos en local.',
      p1: 'El resultado real de este flujo es un lienzo unido, no una escena fusionada. Tres fotos fuente pueden ocupar celdas 16:9 iguales con un hueco blanco y exportarse como una imagen. Ese resultado sirve para comparaciones, hojas de contacto, paneles narrativos y referencias visuales largas.',
      p2: 'Los creadores suelen abrir un unidor de imágenes después de un carrete, una generación Grok o una secuencia de capturas. El objetivo es mantener cada fuente identificable mientras las reúnen en un archivo para compartir, anotar o enviar a otro flujo de Grok Imagine.',
    },
    proof: {
      eyebrow: 'Prueba de salida',
      title: 'Mira lo que crea el unidor de imágenes',
      description:
        'El unidor de imágenes no sintetiza una escena nueva. Crea un lienzo, calcula una celda para cada fuente y dibuja las imágenes en el orden elegido.',
      alt: 'Salida de unir imágenes con tres fotogramas 16:9 en diseño horizontal',
      figcaptionExtra:
        'Cover llena cada celda y puede recortar un borde exterior. Contain conserva la fuente completa y añade fondo alrededor de las imágenes cuya forma difiere del ratio de celda.',
    },
    capabilities: {
      eyebrow: 'Capacidades principales',
      title: '¿Qué puede hacer el unidor de imágenes?',
      description:
        'El unidor de imágenes se centra en controles de diseño repetibles en lugar de edición generativa, para que el arreglo exportado siga siendo predecible.',
    },
    howTo: {
      eyebrow: 'Flujo en cuatro pasos',
      title: 'Cómo unir imágenes online',
      description:
        'La vista previa en vivo se actualiza tras cada cambio de diseño, para revisar el resultado completo antes de la descarga.',
    },
    layoutGuide: {
      eyebrow: 'Guía de diseño',
      title: 'Elige el diseño de imagen adecuado',
      description:
        'Usa la guía del unidor de imágenes para hacer coincidir el lienzo con la forma en que se leerá la imagen final.',
      caption: 'Opciones de diseño horizontal, vertical y en cuadrícula',
      headers: {
        layout: 'Diseño',
        input: 'Mejor entrada',
        output: 'Salida esperada',
        fit: 'Ajuste recomendado',
      },
    },
    specs: {
      eyebrow: 'Especificaciones técnicas',
      title: 'Especificaciones técnicas para unir imágenes',
      description:
        'Estos son los límites actuales de entrada, diseño, procesamiento y exportación que aplica el workbench del navegador.',
      caption:
        'Especificaciones de entrada y exportación del unidor de imágenes',
      headers: {
        parameter: 'Parámetro',
        support: 'Soporte actual',
      },
    },
    useCases: {
      eyebrow: 'Usos prácticos',
      title: 'Casos de uso habituales al unir imágenes',
      description:
        'Usa los mismos controles del unidor de imágenes para comparaciones, referencias largas y colecciones visuales compactas.',
      startingInput: 'Entrada inicial',
      toolAction: 'Acción de la herramienta',
      expectedResult: 'Resultado esperado',
    },
    limits: {
      eyebrow: 'Límites',
      title: 'Límites y controles de calidad',
      description:
        'Ten a la vista Cover, Contain, el tamaño de archivo y el formato de exportación antes de descargar. El workbench no inventa píxeles faltantes ni repara costuras de panorama.',
    },
    faq: {
      eyebrow: 'Preguntas y respuestas',
      title: 'FAQ del unidor de imágenes',
      description:
        'Respuestas directas sobre procesamiento local, calidad de imagen, diseños lado a lado y la diferencia entre unir y fusionar con IA.',
    },
    nextSteps: {
      eyebrow: 'Después de exportar',
      title: 'Continúa con la imagen terminada',
      description:
        'Cuando el unidor de imágenes exporta un lienzo, guarda la descarga en tu dispositivo. Los archivos locales no se almacenan en Historial.',
      body: 'Descarga primero el lienzo y luego abre un flujo vecino si necesitas movimiento o un paso procesado en servidor. Revisa la política de privacidad antes de pasar del ensamblado local a un flujo subido.',
      ariaLabel: 'Continuar con la imagen terminada',
      videoLabel: 'Convertir la imagen unida en vídeo',
      videoDetail: 'Anima la imagen fija una vez descargado el lienzo local.',
      privacyLabel: 'Revisar la política de privacidad',
      privacyDetail:
        'Comprueba la política antes de pasar del ensamblado local a un flujo procesado en servidor.',
    },
    cta: {
      eyebrow: 'Listo para unir',
      title: 'Une tus imágenes ahora',
      body: 'Añade dos archivos o más al unidor de imágenes, elige un diseño predecible, inspecciona la vista previa y descarga un lienzo terminado.',
      button: 'Abrir el unidor de imágenes',
    },
    schema: {
      softwareName: 'Unidor de imágenes',
      actionName: 'Unir imágenes en el navegador',
      howToName: 'Cómo unir imágenes online',
      howToDescription:
        'Añade de 2 a 9 imágenes, elige un diseño, ajusta las celdas y luego previsualiza y descarga un lienzo.',
      breadcrumbHome: 'Inicio',
      breadcrumbPage: 'Unidor de imágenes',
      proofImageName: 'Prueba horizontal 16:9 del unidor de imágenes',
    },
  },
  ui: {
    headerTitle: 'Unidor de imágenes',
    sourceImages: 'Imágenes fuente',
    addImages: 'Añadir imágenes',
    addHint: 'De 2 a 9 archivos JPG, PNG o WebP, hasta 20 MB cada uno',
    chooseFiles: 'Elegir archivos',
    layout: 'Diseño',
    horizontal: 'Horizontal',
    vertical: 'Vertical',
    grid: 'Cuadrícula',
    cellRatio: 'Ratio de celda',
    imageFit: 'Ajuste',
    contain: 'Contain',
    cover: 'Cover',
    gap: 'Hueco',
    background: 'Fondo',
    format: 'Formato',
    stitchDownload: 'Unir y descargar',
    preparingDownload: 'Preparando la descarga...',
    preview: 'Vista previa',
    addAtLeastTwo: 'Añade al menos dos imágenes',
    noPreview: 'Aún no hay vista previa',
    noPreviewHint:
      'El resultado unido aparece aquí en cuanto hay dos imágenes listas.',
    waiting: 'Esperando imágenes',
    livePreviewNote:
      'La vista previa en vivo se reduce a {edge} px. La descarga usa el lienzo de exportación completo.',
    maxImagesError: 'Puedes añadir hasta {count} imágenes.',
    unsupportedTypeError: 'Usa archivos JPG, PNG o WebP.',
    fileTooLargeError: 'Cada imagen debe pesar 20 MB o menos.',
    readFailedError: 'No se pudieron leer las imágenes seleccionadas.',
    exportFailedError: 'No se pudo descargar la imagen unida.',
    loadFailedError: 'No se pudo leer {name} como imagen.',
    previewFailedError: 'Falló la imagen de vista previa.',
    moveEarlier: 'Mover {name} antes',
    moveLater: 'Mover {name} después',
    remove: 'Quitar {name}',
  },
};
